<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { AUTH_ADMIN_INFO } from '@/store/action_types/auth';
import { type FormErrors, isErrorResponse, type EmitsToListeners } from '@/types/core';
//#endregion

//#region types
export interface TimeOption {
  value: number // в секундах
  label: string
}

interface Props {
  significantPeriodList?: TimeOption[] | null
}

interface  Emits {
  'close': [],
};

export interface EditSignificantTtlParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  significantPeriodList: null,
});

const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useTypedStore();
//#endregion

//#region data
const isVisible = ref(false);
const errors = ref<FormErrors>({});
const significantTtl = ref(0);
//#endregion

//#region computed
const adminInfo = computed(() => store.getters['auth/adminInfo']);
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
  significantTtl.value = adminInfo.value.significant_actions_ttl;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};
const handleSave = async () => {
  errors.value = {};
  try {
    const { name, timezone, messenger, messenger_id } = adminInfo.value;

    const result = {
      name,
      timezone,
      messenger: messenger || '',
      messenger_id: messenger_id || '',
      significant_actions_ttl: significantTtl.value,
    };

    const { data: { payload } } = await api.putProfileInfo(result);

    store.commit(`auth/${AUTH_ADMIN_INFO}`, payload);
    store.dispatch('session/initialSessionData');
    handleClose();
  } catch (error) {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  }
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="edit-significant-ttl-popup"
    :title="$t('profile_popup.action_unlock_period')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:flex t:items-start t:mb-16">
        <UiIcon
          :size="24"
          color="var(--text-negative)"
          name="exclamation-triangle"
          class="t:mr-12"
        />
        <div class="edit-significant-ttl-popup__message">
          {{ $t('profile_popup.unlock_period_warning') }}
        </div>
      </div>
      <UiSelectElement
        v-model="significantTtl"
        :label="$t(`profile_popup.action_unlock_period`)"
        :placeholder="$t('profile_popup.action_unlock_period')"
        :list="significantPeriodList"
        size="big"
        option-name-param="label"
        option-label-param="label"
        option-value-param="value"
        filterable
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.edit-significant-ttl-popup {
  &__message {
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;
  }
}
</style>
